const SALT_KEY = 'pin_salt';
const PIN_HASH_KEY = 'pin_hash';

// Generate a random salt
function generateSalt(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Hash a PIN with a salt using PBKDF2 via SubtleCrypto
async function hashPin(pin: string, salt: string): Promise<string> {
  const encoder = new TextEncoder();
  const pinData = encoder.encode(pin + salt);

  const hashBuffer = await crypto.subtle.digest('SHA-256', pinData);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Save a new PIN (hashed + salted)
export async function savePin(pin: string): Promise<void> {
  const salt = generateSalt();
  const hash = await hashPin(pin, salt);
  localStorage.setItem(SALT_KEY, salt);
  localStorage.setItem(PIN_HASH_KEY, hash);
}

// Verify a PIN against the stored hash
export async function verifyPin(pin: string): Promise<boolean> {
  const salt = localStorage.getItem(SALT_KEY);
  const storedHash = localStorage.getItem(PIN_HASH_KEY);
  if (!salt || !storedHash) return false;
  const hash = await hashPin(pin, salt);
  return hash === storedHash;
}

// Check if a PIN has been set
export function hasPinSet(): boolean {
  return !!localStorage.getItem(PIN_HASH_KEY);
}


const AUTH_KEY   = 'app_authenticated';
const LAUNCH_KEY = 'app_launched';        // written BEFORE the reload

/** Call this right before window.location.href = url */
export function setAuthPersisted(): void {
  localStorage.setItem(AUTH_KEY, 'true');
  localStorage.setItem(LAUNCH_KEY, 'true');  // survives reload
}

export function clearAuthPersisted(): void {
  localStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(LAUNCH_KEY);
}

export function isAuthPersisted(): boolean {
  return localStorage.getItem(AUTH_KEY) === 'true';
}

/** 
 * Call once on PWA cold launch.
 * If LAUNCH_KEY is absent → fresh launch → clear auth.
 * If LAUNCH_KEY is present → came from window.location.href → keep auth.
 */
export function handleLaunchAuth(): boolean {
  const launched = sessionStorage.getItem('in_session');
  if (!launched) {
    // Cold PWA launch — new session
    sessionStorage.setItem('in_session', 'true');
    if (!localStorage.getItem(LAUNCH_KEY)) {
      // Not a redirect, genuine cold start → force PIN
      clearAuthPersisted();
      return false;
    }
    // Was a window.location.href redirect → keep auth, clear launch key
    localStorage.removeItem(LAUNCH_KEY);
    return localStorage.getItem(AUTH_KEY) === 'true';
  }
  return localStorage.getItem(AUTH_KEY) === 'true';
}